import React, { useEffect, useState } from 'react'
import { Button } from '@toasttab/shared/src/components'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import Sites from '@toasttab/shared/src/data/Sites.json'
import RegionModal from './RegionModal'
import { calcParentMenuHeight } from '@toasttab/shared/src/components/organisms/Navigation/Mobile/ParentMenus'
import { calcSubMenuHeight } from '@toasttab/shared/src/components/organisms/Navigation/Mobile/SubMenus'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import {
  EntryPathSchema,
  PackageSchema,
  RegionBannerProps,
  SiteSchema
} from './RegionBanner.d'
import RegionSwitcher from './RegionSwitcher'

const updateBannerState = (
  activeBannerCountryCode: string,
  preRegion: string,
  country: string
): boolean => {
  let state = false

  if (
    (preRegion && activeBannerCountryCode !== preRegion) ||
    (!preRegion && activeBannerCountryCode !== country) ||
    (!preRegion && !country)
  ) {
    state = true
  }

  return state
}

const formatUrl = (
  site: SiteSchema,
  packageObj: PackageSchema,
  entryPath: EntryPathSchema
): string => {
  let url = `${site.url}${packageObj.path}${entryPath.uri}`

  // for OTL, set the non-US OTL Homepage
  if (packageObj.path === '/blog/on-the-line' && site.url !== '') {
    url = `${site.url}${packageObj.path}`
  }

  return url.replace(/\/+$/, '')
}

const getRegion = (region: string | undefined) => {
  if (!region) {
    return 'US'
  } else {
    // Check if the region is 'UK' and return 'GB' if it matches
    if (region.toUpperCase() === 'UK') {
      return 'GB'
    }
    // Check if the region matches any site and return the region in uppercase
    return Sites.sites
      .filter((site) => site.defaultRegion)
      .find((site) => site.craftSite === region.toUpperCase())
      ? region.toUpperCase()
      : 'US'
  }
}

const getStringValue = (value: string | null) => {
  return value ? value.toString().toLowerCase() : ''
}

const RegionBanner = ({
  siteId,
  packageName,
  localizedEntries
}: RegionBannerProps) => {
  const [regionData, setRegionData] = useState({
    countryCode: '',
    regionPreference: ''
  })
  const [showRegionBanner, setShowRegionBanner] = useState(false)
  const [showRegionModal, setShowRegionModal] = useState(false)
  const [bannerSelection, setBannerSelection] = useState({
    value: ''
  })

  const { setIsRegionBannerActive } = usePageContext()
  const { geoData, loading } = useGeoLocation()
  const windowSize = useWindowSize()

  const activeSiteBanner = Sites.sites.filter((site) => site.siteId === siteId)
  const packageObj = Sites.packages.find((el) => el.package === packageName)

  useEffect(() => {
    if (geoData) {
      setRegionData({
        countryCode: geoData.country.iso_code,
        regionPreference: geoData.regionPreference.iso_code
      })
    }
  }, [loading])

  useEffect(() => {
    if (regionData.countryCode) {
      const primarySiteLanguageCode = 'us'
      const activeBannerCountryCode: string = activeSiteBanner[0].countryCode
        .toString()
        .toLowerCase()
      let preRegion: string = getStringValue(regionData.regionPreference)
      let country: string = getStringValue(regionData.countryCode)

      let bannerstate = updateBannerState(
        activeBannerCountryCode,
        preRegion,
        country
      )
      setIsRegionBannerActive(bannerstate)
      setShowRegionBanner(bannerstate)

      if (!preRegion && country !== primarySiteLanguageCode) {
        //Preferred Region Cookie is NOT SET AND NON-US USER
        if (
          activeBannerCountryCode === primarySiteLanguageCode ||
          country !== activeBannerCountryCode
        ) {
          setShowRegionModal(true)
          setIsRegionBannerActive(false)
          setShowRegionBanner(false)
        }
      }

      const regionCode = preRegion || country
      Sites.sites
        .filter((site) => site.defaultRegion)
        .forEach((site: SiteSchema) => {
          if (site.countryCode.toString().toLowerCase() === regionCode) {
            const entry = localizedEntries?.find(
              (x) => x.language === site.language
            )
            let slug = ''
            if (entry?.slug || entry?.slugOverride) {
              if (entry.slugOverride) {
                slug = entry.slugOverride.startsWith('/')
                  ? entry.slugOverride
                  : '/' + entry.slugOverride
              } else {
                slug = entry.slug.startsWith('/')
                  ? entry.slug
                  : '/' + entry.slug
              }
            }
            const entryPath = { uri: slug }
            let url = formatUrl(site, packageObj, entryPath)
            setBannerSelection({
              value: url
            })
          }
        })
    }
  }, [regionData])

  const handleAfterSubmitting = (submitedRegion: any) => {
    let parseRegion: Array<string> = submitedRegion.split(/[\/\/]/)
    let region: string = getRegion(parseRegion[1])
    if (region) {
      _helpers.createCookie(`_preferredRegion`, `${region}`, 30, '/')
      setRegionData({
        countryCode: regionData.countryCode,
        regionPreference: region
      })
      window.location.assign(submitedRegion)
    }
  }

  return (
    <>
      {!showRegionBanner && showRegionModal && (
        <RegionModal packageName={packageName} regionData={regionData} />
      )}
      {showRegionBanner && !showRegionModal && (
        <div data-testid='RegionBanner'>
          <div
            className={`bg-default bg-gray-125 py-1 px-4 flex justify-center ${
              showRegionBanner ? '' : 'hidden'
            }`}
          >
            <div
              className='mx-auto py-5 flex-1 md:inline-flex md:items-center lg:max-w-[1200px]'
              data-currentcountrycode=''
            >
              <div className='flex-1 items-center font-sansPro text-white text-center mb-2 md:mb-0 mr-4 w-full md:max-w-[30%] lg:max-w-max'>
                Choose another country or region to see content specific to your
                location
              </div>
              <div className='flex-1 items-center'>
                <select
                  name='regionDropdown'
                  id='region-dropdown'
                  className='mb-2 md:mb-0 mr-4 px-2 py-2 w-full md:w-max md:min-w-[343px] bg-white text-lg placeholder:text-gray-50 text-default focus:outline-none focus:shadow-text-input border border-gray-50 shadow-none rounded-lg'
                  value={bannerSelection.value}
                  onChange={(e) => {
                    setBannerSelection({
                      value: e.target.value
                    })
                  }}
                >
                  {Sites.sites
                    .filter((site) => site.defaultRegion)
                    .map((site: SiteSchema, i: number) => {
                      const entry = localizedEntries?.find(
                        (x) => x.language === site.language
                      )
                      let slug = ''
                      if (entry?.slug || entry?.slugOverride) {
                        if (entry.slugOverride) {
                          slug = entry.slugOverride.startsWith('/')
                            ? entry.slugOverride
                            : '/' + entry.slugOverride
                        } else {
                          slug = entry.slug.startsWith('/')
                            ? entry.slug
                            : '/' + entry.slug
                        }
                      }
                      const entryPath = { uri: slug }
                      let url = formatUrl(site, packageObj, entryPath)

                      return (
                        <option
                          key={`regionItem-${i}`}
                          data-countrycode={site.craftSite}
                          value={url.replace(/\/+$/, '')}
                        >
                          {site.siteName}
                        </option>
                      )
                    })}
                </select>
                <Button
                  variation='primary'
                  fontWeight={['font-semibold']}
                  classes='w-full !h-full md:w-auto py-3 px-6 leading-snug inline-flex items-center justify-center text-center relative transition-all duration-300'
                  data-countrycode=''
                  onClick={() => handleAfterSubmitting(bannerSelection.value)}
                >
                  Continue
                </Button>
              </div>
              <div></div>
              <div
                className='absolute top-[2%] md:top-[5%] lg:top-[12%] right-[2%]'
                aria-label='Close'
                role='button'
              >
                <button
                  className='w-7 before:content-icon-close before:mr-2 before:invert'
                  onClick={() => {
                    setShowRegionBanner(false)
                    setIsRegionBannerActive(false)
                    setTimeout(() => {
                      calcParentMenuHeight(windowSize)
                      calcSubMenuHeight(windowSize)
                    }, 100)
                  }}
                ></button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default RegionBanner
